import { supabase } from '@/lib/supabase';
import type { ChatMessage, ChatTarget } from './types';
import { MESSAGE_SELECT_FIELDS } from './messageSelectFields';
import { filterColumn, targetId } from './chatTarget';

export type FetchBackToResult =
  | {
      status: 'ok';
      /** Oldest -> newest, contiguous from the target through to the newest message. */
      messages: ChatMessage[];
      /** True when the fetch reached the start of the thread, so nothing older exists. */
      reachedStart: boolean;
    }
  | {
      status: 'too-far';
      /** How many messages sit between the target and the bottom of the thread. */
      distance: number;
    };

/**
 * Everything from the newest message back through one older message, in one request.
 *
 * The thread pages backwards 50 at a time, which is right for someone scrolling but wrong
 * for "jump to this pinned message": walking back to a message from three weeks ago means
 * a dozen sequential round trips to find something whose timestamp we already know.
 *
 * So: ask how far back it is, then fetch exactly that far. The count is `head: true`, so
 * it returns a number without reading any rows, and both it and the fetch ride the
 * existing (event_id, created_at) / (direct_chat_id, created_at) /
 * (department_chat_id, created_at) indexes.
 *
 * The result is deliberately CONTIGUOUS FROM THE NEWEST MESSAGE rather than a window
 * around the target. A window would be cheaper for an ancient message, but merging one
 * into the loaded list leaves a hole in the middle that renders as though the messages
 * either side were consecutive -- silently missing history, with no affordance to fill
 * it. Contiguity is what lets the caller merge without inventing gap markers.
 *
 * That contiguity is also the limit: `maxMessages` caps how many rows this will pull,
 * because the message list has no virtualization and every row is a real DOM node. Past
 * the cap it reports `too-far` rather than loading a thousand bubbles to show one.
 */
export async function fetchMessagesBackTo(params: {
  target: ChatTarget;
  /** created_at of the message to reach. */
  createdAt: string;
  maxMessages: number;
  /** Extra older messages beyond the target, so it does not land flush against the top. */
  contextBuffer: number;
}): Promise<FetchBackToResult> {
  const { target, createdAt, maxMessages, contextBuffer } = params;
  const column = filterColumn(target);
  const id = targetId(target);

  // How many messages are newer than the target -- i.e. its distance from the bottom.
  const { count, error: countError } = await supabase
    .from('messages_new')
    .select('id', { count: 'exact', head: true })
    .eq(column, id)
    .gt('created_at', createdAt);

  if (countError) throw countError;

  const distance = count ?? 0;
  if (distance + 1 > maxMessages) return { status: 'too-far', distance };

  // range() is inclusive at both ends, so this asks for the target (at index `distance`)
  // plus contextBuffer older ones. Messages sharing a created_at can shift the target's
  // index by one or two; the buffer absorbs that.
  const limit = distance + contextBuffer;
  const { data, error } = await supabase
    .from('messages_new')
    .select(MESSAGE_SELECT_FIELDS)
    .eq(column, id)
    .order('created_at', { ascending: false })
    .range(0, limit);

  if (error) throw error;

  const rows = (data as ChatMessage[] | null) ?? [];
  return {
    status: 'ok',
    // Oldest -> newest, matching the order the message list renders in.
    messages: rows.slice().reverse(),
    // Fewer rows than asked for means there was nothing older left to give.
    reachedStart: rows.length < limit + 1,
  };
}
